/**
 * Gas Breakdown Panel
 *
 * Shows CO₂ / CH₄ / N₂O totals from the summary byGas data.
 * Each gas row is clickable to navigate to Activities sorted by that gas.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { Loader2, Info } from 'lucide-react';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { Scope2Method, type GasTotals } from '../types/emissions';

interface GasRow {
  key: 'co2' | 'ch4' | 'n2o';
  label: string;
  formula: string;
  bar: string;
  text: string;
}

const GAS_ROWS: GasRow[] = [
  { key: 'co2',  label: 'Carbon Dioxide', formula: 'CO₂',  bar: 'tw:bg-emerald-500', text: 'tw:text-emerald-700' },
  { key: 'ch4',  label: 'Methane',        formula: 'CH₄',  bar: 'tw:bg-blue-500',    text: 'tw:text-blue-700'    },
  { key: 'n2o',  label: 'Nitrous Oxide',  formula: 'N₂O',  bar: 'tw:bg-amber-500',   text: 'tw:text-amber-700'   },
];

interface GasBreakdownPanelProps {
  byGas: GasTotals | undefined;
  isLoading: boolean;
  onGasClick?: (gas: 'co2' | 'ch4' | 'n2o') => void;
  title?: string;
  description?: string;
  scope2Method?: Scope2Method;
}

export const GasBreakdownPanel = ({ byGas, isLoading, onGasClick, title, description, scope2Method }: GasBreakdownPanelProps) => {
  const { formatCompact } = useEmissionsFormatter();

  const hasData = byGas && (byGas.co2 > 0 || byGas.ch4 > 0 || byGas.n2o > 0);
  const gasTotal = byGas ? byGas.co2 + byGas.ch4 + byGas.n2o : 0;

  return (
    <Card className='tw:h-full'>
      <CardHeader>
        <CardTitle className='tw:text-sm tw:font-semibold'>{title ?? 'Gas Breakdown'}</CardTitle>
        <CardDescription>{description ?? 'GWP-weighted CO₂e by gas type — click a row to view top activities'}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='tw:flex tw:justify-center tw:py-6'>
            <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
          </div>
        ) : !hasData ? (
          <div className='tw:flex tw:flex-col tw:items-center tw:py-6 tw:text-muted-foreground'>
            <p className='tw:text-sm'>No gas breakdown data available</p>
            <p className='tw:text-xs tw:mt-1'>Gas breakdown is provided when calculating activities</p>
          </div>
        ) : (
          <div className='tw:space-y-4'>
            {GAS_ROWS.map(({ key, label, formula, bar, text }) => {
              const value = byGas?.[key] ?? 0;
              const pct = gasTotal > 0 ? (value / gasTotal) * 100 : 0;
              const formatted = formatCompact(value);
              const isClickable = !!onGasClick && value > 0;

              return (
                <div
                  key={key}
                  className={cn(
                    'tw:group',
                    isClickable && 'tw:cursor-pointer'
                  )}
                  onClick={isClickable ? () => onGasClick(key) : undefined}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onGasClick(key); } : undefined}
                >
                  <div className='tw:flex tw:justify-between tw:items-baseline tw:mb-1.5'>
                    <div className='tw:flex tw:items-center tw:gap-2'>
                      <span className={cn('tw:text-sm tw:font-semibold tw:w-8', text)}>{formula}</span>
                      <span className={cn(
                        'tw:text-xs tw:text-muted-foreground',
                        isClickable && 'tw:group-hover:text-foreground tw:transition-colors'
                      )}>
                        {label}
                      </span>
                    </div>
                    <div className='tw:text-right'>
                      <span className='tw:text-sm tw:font-semibold'>
                        {formatted.formatted}
                        <span className='tw:text-xs tw:text-muted-foreground tw:ml-1'>{formatted.unit}</span>
                      </span>
                      <span className='tw:text-xs tw:text-muted-foreground tw:ml-2'>{pct.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className='tw:h-2 tw:rounded-full tw:bg-muted tw:overflow-hidden'>
                    <div
                      className={cn('tw:h-full tw:rounded-full tw:transition-all', bar)}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <p className='tw:text-xs tw:text-muted-foreground tw:mt-2'>
              Values are GWP-weighted. Only activities with full gas breakdown data are included.
            </p>
            {scope2Method === Scope2Method.MarketBased && (
              <p className='tw:text-xs tw:text-blue-600 tw:mt-1 tw:flex tw:items-center tw:gap-1'>
                <Info className='tw:size-3 tw:shrink-0' />
                Gas breakdown reflects location-based emission factors.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
